import React from 'react';
import {
  Navigate,
  RouterProvider,
  RouteObject,
  createBrowserRouter,
} from 'react-router-dom';
import { ROUTES, CHILD_ROUTES } from 'src/constants/route';
import LearningPathwayPage from 'src/pages/learning-pathway';
import SearchPage from 'src/pages/searchpage';
import HomePage from 'src/pages/homepage';
import NotFoundPage from 'src/pages/not-found';
import { StrictRouteObject } from 'src/types/reactRouter';
import { FeatureName } from 'src/metrics/metricsFields';
import TopicPage from 'src/pages/topic';
import WebinarPage from 'src/pages/webinar';
import { ClientProvider } from 'src/contexts/ClientContext';
import App from '../App';
import TestApp from '../TestApp';
import RouterError from './RouterError';
import Widget from '../Widget';
import { BiDirectionalRedirect, LegacyModuleRedirect, NewWebinarPageRedirect } from './RouteRedirects';
import LearningModulePage from '../../pages/module';
import LearningModuleCardWidget from '../../pages/widget';
import WidgetIframeTestPage from '../../pages/widget/WidgetIframeTestPage';

// Define a reusable not-found route object
const notFoundRoute = {
  path: '*',
  element: <NotFoundPage />,
  handle: { pageName: FeatureName.NotFoundPage },
};

const routes: StrictRouteObject[] = [
  // Root redirect to home
  {
    path: '/',
    element: <Navigate to={ROUTES.LEARN.HOME} replace />,
    handle: { pageName: FeatureName.RouteRedirect },
    errorElement: <RouterError />,
  },

  // Main application routes
  // **Live in Production**
  {
    path: ROUTES.LEARN.BASE,
    element: <App />,
    handle: { pageName: FeatureName.SuRoot },
    errorElement: <RouterError />,
    children: [
      {
        path: '',
        element: <BiDirectionalRedirect />,
        handle: { pageName: FeatureName.HomePage },
      },
      {
        path: CHILD_ROUTES.SEARCH,
        element: <BiDirectionalRedirect />,
        handle: { pageName: FeatureName.SearchPage },
      },
      // Old module redirect to new new module page.
      // For now, /learn/courses will not be routing to seller university website,
      // this will only work when we change the route of this path
      // from SellerUniversityMons to SellerUniversityWebsite
      {
        path: CHILD_ROUTES.COURSES,
        element: <LegacyModuleRedirect />,
        handle: { pageName: FeatureName.RouteRedirect },
      },
      {
        path: `${CHILD_ROUTES.LEARNING_PATHWAY}/:learningPathwayId`,
        element: <LearningPathwayPage />,
        handle: { pageName: FeatureName.LearningPathwayPage },
      },
      {
        path: `${CHILD_ROUTES.TOPIC}/:topicId`,
        element: <TopicPage />,
        handle: { pageName: FeatureName.TopicPage },
      },
      {
        path: `${CHILD_ROUTES.LEARNING_MODULE}/:learningModuleId`,
        element: <LearningModulePage />,
        handle: { pageName: FeatureName.LearningModulePage },
      },
      {
        path: CHILD_ROUTES.WEBINAR,
        element: <WebinarPage />,
        handle: { pageName: FeatureName.WebinarPage },
      },
      {
        path: CHILD_ROUTES.WEBINAR_JOIN,
        element: <NewWebinarPageRedirect />,
        handle: { pageName: FeatureName.RouteRedirect },
      },
    ],
  },

  // New application routes used for campus redesign
  // **Gated by mons migration weblab and now in live in development only**
  {
    path: ROUTES.NEW_LEARN.BASE,
    element: <App />,
    handle: { pageName: FeatureName.SuRoot },
    errorElement: <RouterError />,
    children: [
      {
        path: '',
        element: <HomePage />,
        handle: { pageName: FeatureName.HomePage },
      },
      {
        path: CHILD_ROUTES.SEARCH,
        element: <SearchPage />,
        handle: { pageName: FeatureName.SearchPage },
      },
      {
        path: `${CHILD_ROUTES.LEARNING_PATHWAY}/:learningPathwayId`,
        element: <BiDirectionalRedirect />,
        handle: { pageName: FeatureName.RouteRedirect },
      },
      {
        path: `${CHILD_ROUTES.TOPIC}/:topicId`,
        element: <BiDirectionalRedirect />,
        handle: { pageName: FeatureName.RouteRedirect },
      },
      {
        path: `${CHILD_ROUTES.LEARNING_MODULE}/:learningModuleId`,
        element: <BiDirectionalRedirect />,
        handle: { pageName: FeatureName.RouteRedirect },
      },
      notFoundRoute,
    ],
  },

  // Test application routes with custom header
  // For testing in beta end to end for routes not yet productionized
  {
    path: ROUTES.TEST_LEARN.BASE,
    element: <TestApp />,
    handle: { pageName: FeatureName.SuRoot },
    errorElement: <RouterError />,
    children: [
      {
        path: '',
        element: <HomePage />,
        handle: { pageName: FeatureName.HomePage },
      },
      {
        path: `${CHILD_ROUTES.LEARNING_PATHWAY}/:learningPathwayId`,
        element: <LearningPathwayPage />,
        handle: { pageName: FeatureName.LearningPathwayPage },
      },
      {
        path: CHILD_ROUTES.SEARCH,
        element: <SearchPage />,
        handle: { pageName: FeatureName.SearchPage },
      },
      {
        path: `${CHILD_ROUTES.TOPIC}/:topicId`,
        element: <TopicPage />,
        handle: { pageName: FeatureName.TopicPage },
      },
      {
        path: `${CHILD_ROUTES.LEARNING_MODULE}/:learningModuleId`,
        element: <LearningModulePage />,
        handle: { pageName: FeatureName.LearningModulePage },
      },
      {
        path: `${CHILD_ROUTES.WIDGET}/:learningModuleId`,
        element: <LearningModuleCardWidget />,
        handle: { pageName: FeatureName.LearningModuleCardWidget },
      },
      {
        path: CHILD_ROUTES.WIDGET_IFRAME_TEST,
        element: <WidgetIframeTestPage />,
        handle: { pageName: FeatureName.LearningModuleCardWidget },
      },
      {
        path: CHILD_ROUTES.WEBINAR,
        element: <WebinarPage />,
        handle: { pageName: FeatureName.WebinarPage },
      },
      notFoundRoute,
    ],
  },

  // Widget routes - separate from main app structure
  {
    path: ROUTES.WIDGETS.BASE,
    element: <Widget />,
    handle: { pageName: FeatureName.WidgetRoot },
    errorElement: <RouterError />,
    children: [
      {
        path: `${CHILD_ROUTES.LEARNING_PATHWAY}/:learningPathwayId`,
        element: <LearningPathwayPage />,
        handle: { pageName: FeatureName.LearningPathwayWidget },
      },
      {
        path: `${CHILD_ROUTES.LEARNING_MODULE}/:learningModuleId`,
        element: <LearningModulePage />,
        handle: { pageName: FeatureName.LearningModulePage },
      },
      {
        path: `${CHILD_ROUTES.LEARNING_MODULE_CARD}/:learningModuleId`,
        element: <LearningModuleCardWidget />,
        handle: { pageName: FeatureName.LearningModuleCardWidget },
      },
      notFoundRoute,
    ],
  },

  // Global catch-all route for requests outside of defined route structures
  // Redirects to main app base route for local development servers, test environments, etc.
  // In production, this should never be triggered since all valid routes are under /learn/*
  // and have their own NotFoundPage handling via the notFoundRoute constant above
  {
    path: '*',
    element: <Navigate to={ROUTES.LEARN.BASE} replace />,
    handle: { pageName: FeatureName.RouteRedirect },
    errorElement: <RouterError />,
  },
];

const ReactRouterProvider = () => {
  return (
    <ClientProvider>
      <RouterProvider router={createBrowserRouter(routes as RouteObject[])} />
    </ClientProvider>
  );
};

export default ReactRouterProvider;
